import { useState, useRef, KeyboardEvent, ChangeEvent } from 'react'
import { useModalBehavior } from '../../hooks/useModalBehavior'
import './TagInput.css'

interface TagInputProps {
    value?: string[]
    onChange: (tags: string[]) => void
    suggestions?: string[]
    placeholder?: string
    maxTags?: number
    maxLength?: number
    disabled?: boolean
    dataTestId?: string
    id?: string
}

export default function TagInput({
    value = [],
    onChange,
    suggestions = [],
    placeholder = 'Type and press Enter...',
    maxTags = 10,
    maxLength = 100,
    disabled = false,
    dataTestId,
    id,
}: Readonly<TagInputProps>) {
    const [inputValue, setInputValue] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1)
    const inputRef = useRef<HTMLInputElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const safeValue = Array.isArray(value) ? value : []

    const filteredSuggestions = suggestions
        .filter(
            (suggestion) =>
                suggestion &&
                typeof suggestion === 'string' &&
                suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
                !safeValue.includes(suggestion) &&
                suggestion !== inputValue
        )
        .slice(0, 5)

    useModalBehavior({ modalRef: containerRef, onClose: () => setShowSuggestions(false) })

    const addTag = (tag: string) => {
        if (!tag?.trim()) return

        const trimmedTag = tag.trim()

        if (value.some((t) => t.toLowerCase() === trimmedTag.toLowerCase())) {
            setInputValue('')
            return
        }

        if (value.length >= maxTags) {
            setInputValue('')
            return
        }

        const truncatedTag = trimmedTag.length > maxLength ? trimmedTag.substring(0, maxLength) : trimmedTag

        onChange([...value, truncatedTag])
        setInputValue('')
        setShowSuggestions(false)
        setFocusedSuggestionIndex(-1)
    }

    const removeTag = (indexToRemove: number) => {
        onChange(safeValue.filter((_, index) => index !== indexToRemove))
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === 'Enter' || e.key === ',') && focusedSuggestionIndex === -1) {
            e.preventDefault()
            addTag(inputValue)
        } else if (e.key === 'Enter' && focusedSuggestionIndex >= 0) {
            e.preventDefault()
            addTag(filteredSuggestions[focusedSuggestionIndex])
        } else if (e.key === 'Backspace' && !inputValue && safeValue.length > 0) {
            removeTag(safeValue.length - 1)
        } else if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
            e.preventDefault()
            setFocusedSuggestionIndex((prev) => (prev < filteredSuggestions.length - 1 ? prev + 1 : prev))
        } else if (e.key === 'ArrowUp' && filteredSuggestions.length > 0) {
            e.preventDefault()
            setFocusedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1))
        }
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value)
        setShowSuggestions(true)
        setFocusedSuggestionIndex(-1)
    }

    const handleSuggestionClick = (suggestion: string) => {
        addTag(suggestion)
        inputRef.current?.focus()
    }

    return (
        <div className={`tag-input-container ${disabled ? 'disabled' : ''}`} ref={containerRef}>
            <div className="tag-input-wrapper">
                {safeValue.map((tag, index) => (
                    <span key={`${tag}-${index}`} className="tag">
                        <span className="tag-text">{tag}</span>
                        {!disabled && (
                            <button type="button" className="tag-remove" onClick={() => removeTag(index)} aria-label={`Remove ${tag}`}>
                                ×
                            </button>
                        )}
                    </span>
                ))}
                <input
                    id={id}
                    ref={inputRef}
                    data-testid={dataTestId}
                    type="text"
                    className="tag-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder={safeValue.length === 0 ? placeholder : ''}
                    disabled={disabled || safeValue.length >= maxTags}
                    maxLength={maxLength}
                />
            </div>

            {!disabled && showSuggestions && filteredSuggestions.length > 0 && (
                <div className="tag-suggestions">
                    {filteredSuggestions.map((suggestion, index) => (
                        <button
                            key={`${suggestion}-${index}`}
                            type="button"
                            className={`tag-suggestion-item ${index === focusedSuggestionIndex ? 'focused' : ''}`}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onMouseEnter={() => setFocusedSuggestionIndex(index)}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}

            {!disabled && safeValue.length > 0 && (
                <div className="tag-count">
                    {safeValue.length}/{maxTags}
                </div>
            )}
        </div>
    )
}
